import React, { useState, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  LinearProgress,
} from '@mui/material';
import {
  CloudUpload,
  InsertDriveFile,
  Image as ImageIcon,
  Description,
} from '@mui/icons-material';
import { doclingService } from '../../services/doclingService';

interface FileAttachmentDialogProps {
  open: boolean;
  onClose: () => void;
  onAttach: (attachment: ProcessedAttachment) => void;
}

export interface ProcessedAttachment {
  fileName: string;
  fileType: string;
  attachmentType: 'image' | 'text';
  imageData?: string; // Base64 encoded image data with data URL prefix
  textContent?: string; // Plain text or extracted document content
}

const isImageFile = (type: string): boolean => {
  return type.startsWith('image/');
};

const isPlainTextFile = (type: string): boolean => {
  return type === 'text/plain' || type === 'text/markdown' || type === 'text/csv';
};

export const FileAttachmentDialog: React.FC<FileAttachmentDialogProps> = ({
  open,
  onClose,
  onAttach,
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const processFile = async (file: File) => {
    setProcessing(true);
    setError(null);
    setSelectedFile(file);

    try {
      const mimeType = file.type;

      // Handle images
      if (isImageFile(mimeType)) {
        const base64 = await fileToBase64(file);
        console.log('[FileAttachmentDialog] Image processed:', {
          fileName: file.name,
          fileType: mimeType,
          base64Length: base64.length,
          base64Preview: base64.substring(0, 100),
        });
        const attachment: ProcessedAttachment = {
          fileName: file.name,
          fileType: mimeType,
          attachmentType: 'image',
          imageData: base64,
        };
        onAttach(attachment);
        handleClose();
        return;
      }

      // Handle plain text files
      if (isPlainTextFile(mimeType)) {
        const textContent = await file.text();
        const attachment: ProcessedAttachment = {
          fileName: file.name,
          fileType: mimeType,
          attachmentType: 'text',
          textContent,
        };
        onAttach(attachment);
        handleClose();
        return;
      }

      // Handle documents (PDF, PPTX, DOCX, XLSX)
      const isSupported = doclingService.isSupportedDocumentType(mimeType) || 
                         doclingService.isSupportedByExtension(file.name);

      if (isSupported) {
        setExtracting(true);
        try {
          const extractedText = await doclingService.convertFileToMarkdown(file);
          const attachment: ProcessedAttachment = {
            fileName: file.name,
            fileType: mimeType,
            attachmentType: 'text',
            textContent: extractedText,
          };
          onAttach(attachment);
          handleClose();
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to extract text from document');
        } finally {
          setExtracting(false);
        }
        return;
      }

      // Unsupported file type
      setError('Unsupported file type. Please upload an image, plain text file, PDF, PowerPoint, Word document, or Excel spreadsheet.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to process file');
    } finally {
      setProcessing(false);
    }
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === 'string') {
          resolve(reader.result);
        } else {
          reject(new Error('Failed to read file as base64'));
        }
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  };

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        const file = e.dataTransfer.files[0];
        await processFile(file);
      }
    },
    [processFile]
  );

  const handleFileInput = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      await processFile(file);
    }
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const handleClose = () => {
    setDragActive(false);
    setProcessing(false);
    setExtracting(false);
    setError(null);
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  const getFileIcon = () => {
    if (!selectedFile) return <InsertDriveFile sx={{ fontSize: 48, color: 'text.secondary' }} />;
    
    if (isImageFile(selectedFile.type)) {
      return <ImageIcon sx={{ fontSize: 48, color: 'primary.main' }} />;
    }
    return <Description sx={{ fontSize: 48, color: 'primary.main' }} />;
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Attach File</DialogTitle>
      <DialogContent>
        <input
          ref={fileInputRef}
          type="file"
          hidden
          onChange={handleFileInput}
          accept="image/*,.pdf,.pptx,.ppt,.docx,.doc,.xlsx,.xls,.txt,.md,.csv"
        />

        <Paper
          sx={{
            p: 4,
            border: dragActive ? '2px dashed' : '2px dashed',
            borderColor: dragActive ? 'primary.main' : 'grey.300',
            bgcolor: dragActive ? 'action.hover' : 'background.default',
            cursor: 'pointer',
            transition: 'all 0.3s',
            '&:hover': {
              borderColor: 'primary.main',
              bgcolor: 'action.hover',
            },
          }}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={handleBrowseClick}
        >
          <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
            {processing ? (
              <>
                {getFileIcon()}
                <CircularProgress size={40} />
                {extracting ? (
                  <Box sx={{ width: '100%', textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Extracting text from document...
                    </Typography>
                    <LinearProgress 
                      sx={{ 
                        mt: 2,
                        height: 8,
                        borderRadius: 4,
                        '& .MuiLinearProgress-bar': {
                          animation: 'pulse 1.5s ease-in-out infinite',
                          '@keyframes pulse': {
                            '0%': { opacity: 0.6 },
                            '50%': { opacity: 1 },
                            '100%': { opacity: 0.6 },
                          },
                        },
                      }} 
                    />
                    {selectedFile && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        {selectedFile.name}
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Processing file...
                  </Typography>
                )}
              </>
            ) : (
              <>
                <CloudUpload sx={{ fontSize: 64, color: 'primary.main' }} />
                <Typography variant="h6" color="text.primary">
                  Drop file here or click to browse
                </Typography>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  Supported files: Images, PDFs, PowerPoint, Word, Excel, and plain text
                </Typography>
              </>
            )}
          </Box>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        <Box mt={2}>
          <Typography variant="caption" color="text.secondary">
            <strong>How it works:</strong>
          </Typography>
          <Typography variant="caption" color="text.secondary" component="div">
            • Images are attached directly to your message
          </Typography>
          <Typography variant="caption" color="text.secondary" component="div">
            • Plain text files are included in your prompt
          </Typography>
          <Typography variant="caption" color="text.secondary" component="div">
            • Documents (PDF, Office files) are automatically extracted and included as text
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={processing}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

